import { Autocomplete, Box, FormControlLabel, Icon, TextField, Typography } from "@mui/material";
import { BreadCrumb, SnackSimple } from "../../../../../../components";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DinamicForm } from "../../../../../../components/dinamicForm";
import { useTimeConfigContext } from "../../context";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"
import { createServiceLevelSchema } from "../../../../../../utils/schema";
import { options, useGradientColor } from "../../utils/constants";
import { TimePriority } from "../../../../../../components/timePriority";
import { LoadingButton } from "@mui/lab";
import { useStyles } from "./styles";
import { TimeType } from "../../utils/enum/time";
import { generalDisabled } from "../../../../../../utils/validate/validate";
import { IOSSwitch } from "../../../../../../components/dynamicFormRef/switch";
import { formInputState } from "../../utils/hooks/input";
import { useFilter } from "../../../../../../hooks/filter";
import { validateItems } from "../../utils/formater";
import { FormInputs, FormTime } from "../../utils/types";
import { formatBodyToCreateOrEdit } from "../../utils/formater/form";
import { levelServiceFetch } from "../../../../../../utils/constants/callService";
import { ModalType, SnackbarFormat } from "../../../../../../utils/types/generals";
import { Types } from "../../../../../../utils/enums";
import { handleAllErrors } from "../../../../../../utils/methods/handleErrors";
import SimpleModal from "../../../../../../components/modal/simpleModal";
import Loader from "../../../../../../components/loader";
import { icons } from "../../../../../../utils/icons";

export function FormEdit() {
    const { service, item } = useTimeConfigContext()
    const navigate = useNavigate()
    const [loadings, setLoadings] = useState({ form: false })
    const [oldCategories, setOldCategories] = useState<string[]>([])
    const gradientColor = useGradientColor()
    const [formInput, setFormInput] = useState(formInputState)
    const [snackbar, setSnackbar] = useState<SnackbarFormat>({ message: '', open: false, type: Types.SUCCESS })
    const [modal, setModal] = useState<ModalType>()
    const filterForm = useFilter({
        formatFilter: {},
        externalValidation: (payload) => {
            const { localeBody, depatmentList } = validateItems({ ...payload, departmentItem: item.departments })
            if (payload.name === 'areas') {
                setFormInput(prev => ({ ...prev, raiting: { ...prev.raiting, departments: { ...prev.raiting.departments, items: depatmentList } } }))
            }
            return localeBody
        }
    })
    const { id } = useParams()
    const { t } = useTranslation()
    const styles = useStyles()
    const timesBySla = useMemo<FormTime>(() => {
        const defaultData = gradientColor.map(item => ({
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            priorityId: item.priority,
        }))

        return {
            [TimeType.SLA]: defaultData,
            [TimeType.OLA]: defaultData,
            [TimeType.MAX_TIME]: false,
            [TimeType.MAX_TIME_HOUR]: options[0]
        }
    }, [])

    const form = useForm({
        mode: 'onChange',
        resolver: yupResolver(createServiceLevelSchema) as any,
        defaultValues: timesBySla,
    })

    const isOlaDisabled = form.watch("ola_switch_firstResponseHours");
    const isSlaDisabled = form.watch("sla_switch_firstResponseHours");

    const disableForm = useMemo(() => {
        const inputs = generalDisabled({ ...formInput.description, ...formInput.raiting }, filterForm.filterData)
        return inputs
    }, [filterForm.filterData])

    const saveForm = async () => {
        if (!id) { return }
        const body = formatBodyToCreateOrEdit({ formInputs: filterForm.filterData as FormInputs, formTime: form.getValues(), oldCategories })
        const response = await levelServiceFetch.updateServiceLevel({ body, id })
        if (!!response.errors) {
            const code = response?.errors[0].code
            setSnackbar({
                open: true,
                message: handleAllErrors(code, t, "general.serviceTime.time.form.snackbar.update.error"),
                type: Types.ERROR
            })
            return
        }
        setModal({
            open: true,
            title: t("general.serviceTime.time.form.snackbar.update.success"),
            description: '',
            icon: <Icon className="icon-size" sx={styles.checkIcon}>{icons('check-outlined')}</Icon>
        })
    }

    const closeSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }))
    }
    const confirm = () => {
        navigate('/dashboard/report-folio/time-config')
    }

    const getServiceLevelById = async () => {
        if (!id) { return }
        setLoadings(prev => ({ ...prev, form: true }))
        const { format, } = await levelServiceFetch.getServiceLevelById({ id })
        const department = await service.getDepartmentService({ form: true })
        setLoadings(prev => ({ ...prev, form: false }))
        filterForm.handleDefaultValues({ defaultValue: format?.formInputs ?? {} })
        const areas = format?.formInputs?.areas
        form.reset(format?.formTime)
        setOldCategories(format?.formInputs?.section?.map(categorie => categorie.value) ?? [])
        setFormInput(prev => ({
            ...prev,
            raiting: {
                ...prev.raiting,
                departments: { ...prev.raiting.departments, items: department?.filter((department) => department.areaId == areas?.value) },
            }
        }))
    }
    useLayoutEffect(() => {
        service.getAreasService({ form: true })
        service.getCategories({ form: true })
        getServiceLevelById()
    }, [])

    useEffect(() => {
        setFormInput(prev => ({
            ...prev,
            raiting: {
                ...prev.raiting,
                areas: { ...prev.raiting.areas, items: item.areas },
                section: { ...prev.raiting.section, items: item.section }
            }
        }))
    }, [item.section, item.areas])
    if (!!loadings.form) { return <Loader /> }
    return (
        <Box className="main-container">
            <BreadCrumb
                removeBreadcrumb={['report-folio']}
                replaceLabels={{
                    form: 'timeConfigCreate',
                    formEdit: 'timeConfigCreate'
                }}
            />

            <Typography mt={2} variant="subtitle_grey" color='secondary.main' fontWeight='bold'>{t(`general.serviceTime.time.form.titleEdit`)}</Typography>

            <Typography my={2} variant="subtitle_grey" color='secondary.main' fontWeight='bold'>{t('general.serviceTime.time.form.generalDescription')}</Typography>

            <DinamicForm input={Object.values(formInput.description)} onChange={filterForm.onChange} values={filterForm.filterData} />

            <Typography my={2} variant="subtitle_grey" color='secondary.main' fontWeight='bold'>{t('general.serviceTime.time.form.classificationCriteria')}</Typography>

            <DinamicForm justifyContentLeft input={Object.values(formInput.raiting)} onChange={filterForm.onChange} values={filterForm.filterData} />

            <Box sx={styles.times}>
                <TimePriority
                    gradientColor={gradientColor}
                    name={TimeType.SLA}
                    disable={!!isSlaDisabled}
                    form={form}
                    timesBySla={gradientColor}
                    subtitle={t('general.serviceTime.time.form.nextTime')}
                    title={t('general.serviceTime.time.form.slaResolution')}
                    switchLabel={t("general.serviceTime.time.form.ticketPriority")}
                />

                <TimePriority
                    gradientColor={gradientColor}
                    name={TimeType.OLA}
                    disable={!!isOlaDisabled}
                    form={form}
                    timesBySla={gradientColor}
                    subtitle={t('general.serviceTime.time.form.nextTime')}
                    title={t('general.serviceTime.time.form.olaResolution')}
                    switchLabel={t("general.serviceTime.time.form.ticketPriority")}
                />

                <Box sx={styles.container}>
                    <Typography my={2} variant="subtitle_grey" color='secondary.main' fontWeight='bold'>{t('general.serviceTime.time.form.maximumTimeForAutomaticClosing')}</Typography>

                    <Controller
                        control={form.control}
                        name={TimeType.MAX_TIME}
                        render={({ field }) => (
                            <FormControlLabel
                                {...field}
                                control={<IOSSwitch checked={field.value} />}
                                label={t("general.serviceTime.time.form.userInChargeManageRequests")}
                                sx={styles.switch}
                            />
                        )}
                    />

                    <Box sx={styles.containerSelect}>
                        <Controller
                            control={form.control}
                            name={TimeType.MAX_TIME_HOUR}
                            render={(field) => (
                                <Autocomplete
                                    {...field}
                                    options={options}
                                    onChange={(_, value) => {
                                        if (!value) {
                                            field.field.onChange(options[0])
                                            return
                                        }
                                        field.field.onChange(value)
                                    }}
                                    value={field.field.value ?? options[0]}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('general.serviceTime.time.form.input.closingTime')}
                                        />
                                    )}

                                />
                            )}
                        />
                    </Box>
                </Box>

                <Box sx={styles.footer}>
                    <LoadingButton onClick={saveForm} disabled={disableForm} variant="contained">{t('general.save')}</LoadingButton>
                </Box>
            </Box>
            <SimpleModal
                open={!!modal?.open}
                close={modal?.confirm || confirm}
                title={modal?.title}
                description={modal?.description}
                buttonText={modal?.confirmText ?? t('general.confirm')}
                next={modal?.confirm || confirm}
                children={modal?.icon}
                containerBox={{ sx: styles.modalContainer }}
            />
            <SnackSimple close={closeSnackbar} open={snackbar.open} type={snackbar.type} message={snackbar.message} />
        </Box>
    )
}